// frontend/src/widgets/alert_widget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-23
// Updated:      2026-09-07 13:36 BST
//
// Description:
//   Dashboard widget that displays live, dismissible alerts using shared context.
//
// Mobile Responsiveness (2026-09-07):
//   - Preserves the existing desktop alert list and 340px desktop scroll cap.
//   - Presents one complete alert per mobile viewport, measured from the actual
//     rendered alert height so wrapped titles/messages are never clipped.
//   - Expands the nearest ReactGridLayout host on mobile when required because
//     mobile widgets do not expose the desktop resize handle.
//   - Uses one mounted alert list only; no duplicate mobile alert components.
//   - Adds mobile scroll snapping and touch-safe dismiss controls.
//   - Leaves alert context, severity ordering, and dismiss behaviour unchanged.

import React, { useEffect, useRef, useState } from 'react';
import {
  FaExclamationCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBell,
  FaTimes
} from 'react-icons/fa';
import { useAlerts } from '../context/AlertContext';

const MOBILE_BREAKPOINT = 768;
const MOBILE_EMPTY_HEIGHT = 48;

const AlertWidget = () => {
  const { alerts, dismissAlert } = useAlerts();

  const rootRef = useRef(null);
  const firstAlertRef = useRef(null);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(null);

  const alertStyles = {
    critical: {
      bg: '#FAEAE8',
      border: '#C9190B',
      icon: '#C9190B',
      text: '#7D1007',
      iconEl: <FaExclamationCircle />
    },
    warning: {
      bg: '#FDF7E7',
      border: '#F0AB00',
      icon: '#F0AB00',
      text: '#866618',
      iconEl: <FaExclamationTriangle />
    },
    success: {
      bg: '#F3FAF2',
      border: '#3E8635',
      icon: '#3E8635',
      text: '#1E4F18',
      iconEl: <FaCheckCircle />
    },
    info: {
      bg: '#E7F1FA',
      border: '#2B9AF3',
      icon: '#2B9AF3',
      text: '#024469',
      iconEl: <FaBell />
    }
  };

  const priority = { critical: 1, warning: 2, success: 3, info: 4 };
  const sorted = [...alerts].sort(
    (a, b) => priority[a.severity] - priority[b.severity]
  );

  const firstAlert = sorted[0] || null;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest('.react-grid-item');
    const previousGridHeight = gridHost?.style.height || '';
    const previousGridMinHeight = gridHost?.style.minHeight || '';

    let resizeObserver = null;
    let animationFrame = null;

    const restoreDesktopHost = () => {
      if (!gridHost) return;
      gridHost.style.height = previousGridHeight;
      gridHost.style.minHeight = previousGridMinHeight;
    };

    const measure = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) {
          setMobileViewportHeight(null);
          restoreDesktopHost();
          return;
        }

        const card = firstAlertRef.current;
        const requiredViewportHeight = card
          ? Math.ceil(card.getBoundingClientRect().height)
          : MOBILE_EMPTY_HEIGHT;

        setMobileViewportHeight(requiredViewportHeight);

        if (gridHost) {
          const hostRect = gridHost.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const hostChrome = Math.max(0, hostRect.height - rootRect.height);
          const requiredHostHeight = Math.ceil(
            requiredViewportHeight + hostChrome
          );

          gridHost.style.height = `${requiredHostHeight}px`;
          gridHost.style.minHeight = `${requiredHostHeight}px`;
        }
      });
    };

    measure();
    window.addEventListener('resize', measure);

    if (typeof ResizeObserver !== 'undefined' && firstAlertRef.current) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(firstAlertRef.current);
    }

    return () => {
      window.removeEventListener('resize', measure);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      restoreDesktopHost();
    };
  }, [
    firstAlert?.id,
    firstAlert?.severity,
    firstAlert?.title,
    firstAlert?.message,
    sorted.length
  ]);

  const handleDismiss = (id) => {
    dismissAlert(id);
  };

  const mobileHeightStyle =
    mobileViewportHeight !== null
      ? {
          height: `${mobileViewportHeight}px`,
          minHeight: `${mobileViewportHeight}px`,
          maxHeight: `${mobileViewportHeight}px`
        }
      : undefined;

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 w-full flex-col gap-3 overflow-y-auto overflow-x-hidden overscroll-contain snap-y snap-mandatory md:max-h-[340px] md:snap-none"
      style={mobileHeightStyle}
    >
      {sorted.map((alert, index) => {
        const style = alertStyles[alert.severity];

        return (
          <div
            key={alert.id}
            ref={index === 0 ? firstAlertRef : null}
            className="relative flex w-full shrink-0 snap-start snap-always items-start gap-3 rounded-md px-3 py-3 pr-12 shadow-sm md:snap-none md:px-4 md:pr-10"
            style={{
              backgroundColor: style.bg,
              borderTop: `3px solid ${style.border}`
            }}
          >
            <div
              className="shrink-0 pt-0.5"
              style={{ color: style.icon, fontSize: '1.25rem' }}
            >
              {style.iconEl}
            </div>

            <div className="min-w-0 flex-grow">
              <div
                className="break-words font-bold"
                style={{ color: style.text }}
              >
                {alert.title}
              </div>

              {alert.message && (
                <div
                  className="mt-0.5 break-words"
                  style={{ color: style.text, fontSize: '0.85rem' }}
                >
                  {alert.message}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleDismiss(alert.id)}
              title="Dismiss"
              aria-label={`Dismiss alert${alert.title ? `: ${alert.title}` : ''}`}
              className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center md:right-1 md:top-1 md:h-8 md:w-8"
              style={{
                background: 'none',
                border: 'none',
                color: style.text,
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        );
      })}

      {sorted.length === 0 && (
        <div
          ref={firstAlertRef}
          className="flex min-h-12 shrink-0 items-center justify-center text-center text-sm text-gray-500"
        >
          No current alerts
        </div>
      )}
    </div>
  );
};

export default AlertWidget;
